import { calendar, forDate, publicationDates } from './content/calendar.ts';
import { mapIndex } from './content/map-index.ts';
import { attemptKey } from './domain.ts';
import { categories, subjects } from './types.ts';
import type { Progress, Subject } from './types.ts';

/** Complete authored dates; public navigation additionally gates on the local day. */
export const authoredDates = publicationDates.filter(
  (date) =>
    categories.every((category) => forDate(date, category)?.questions.length) && mapIndex[date],
);
export function availableDates(today: string, dates = authoredDates): string[] {
  return dates.filter((date) => date <= today);
}
export function defaultDate(today: string, dates = authoredDates): string {
  // Before the first publication, App shows a no-published-practice state.
  return availableDates(today, dates).at(-1) ?? today;
}
export function selectedSubjects(progress: Progress, date: string): Subject[] {
  return progress.dailySubjects?.[date] ?? subjects;
}
export function navigationSubjects(progress: Progress, date: string): Subject[] {
  return progress.dailyConfirmed?.[date] ? selectedSubjects(progress, date) : subjects;
}
/** Calendar cells are explicit publication/progress states, never new practice routes. */
export function calendarDay(progress: Progress, date: string, today: string, mapDone: boolean) {
  const availability = !authoredDates.includes(date)
    ? 'unavailable'
    : date > today
      ? 'scheduled'
      : 'available';
  const selected =
    availability === 'available' && progress.dailyConfirmed?.[date]
      ? selectedSubjects(progress, date)
      : [];
  const completed = selected.filter((subject) => subjectComplete(progress, date, subject, mapDone));
  return {
    availability,
    selected,
    completed,
    done: selected.length > 0 && completed.length === selected.length,
    reward: availability === 'available' ? completionReward(progress, date, mapDone) : null,
  };
}
export function monthCells(month: string): (string | null)[] {
  const first = new Date(`${month}-01T12:00:00Z`);
  const days = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  const cells: (string | null)[] = Array.from({ length: first.getUTCDay() }, () => null);
  for (let day = 1; day <= days; day++) cells.push(`${month}-${String(day).padStart(2, '0')}`);
  while (cells.length % 7) cells.push(null);
  return cells;
}
export function toggleSubject(progress: Progress, date: string, subject: Subject): Progress {
  const selected = selectedSubjects(progress, date);
  const next = selected.includes(subject)
    ? selected.filter((item) => item !== subject)
    : subjects.filter((item) => item === subject || selected.includes(item));
  return { ...progress, dailySubjects: { ...progress.dailySubjects, [date]: next } };
}
/** Committing a selection never resets answers, history or an already earned stamp. */
export function confirmSubjects(progress: Progress, date: string, chosen: Subject[]): Progress {
  const selected = subjects.filter((subject) => chosen.includes(subject));
  if (!selected.length) return progress;
  return {
    ...progress,
    dailySubjects: { ...progress.dailySubjects, [date]: selected },
    dailyConfirmed: { ...progress.dailyConfirmed, [date]: true },
  };
}
export type RewardKind = 'sun' | 'moon';
export function completionReward(
  progress: Progress,
  date: string,
  mapDone: boolean,
): RewardKind | null {
  if (!progress.dailyConfirmed?.[date] || !dailyChecklist(progress, date, mapDone).done)
    return null;
  return subjects.every((subject) => subjectComplete(progress, date, subject, mapDone))
    ? 'sun'
    : 'moon';
}
export function collectedStamp(
  progress: Progress,
  date: string,
  mapDone: boolean,
): RewardKind | null {
  const saved = progress.dailyStamps?.[date];
  if (!saved) return null;
  // Boolean stamps from the previous version are classified from retained progress.
  return saved === 'sun' || completionReward(progress, date, mapDone) === 'sun' ? 'sun' : 'moon';
}
export function collectDailyStamp(progress: Progress, date: string, mapDone: boolean): Progress {
  const reward = completionReward(progress, date, mapDone);
  const saved = progress.dailyStamps?.[date];
  if (!reward || saved === 'sun' || saved === reward) return progress;
  return { ...progress, dailyStamps: { ...progress.dailyStamps, [date]: reward } };
}
export function subjectComplete(
  progress: Progress,
  date: string,
  subject: Subject,
  mapDone: boolean,
): boolean {
  if (subject === 'maps') return mapDone;
  const set = calendar[date] && forDate(date, subject);
  return (
    !!set &&
    set.questions.every((question) => !!progress.attempts[attemptKey(`daily:${date}`, question.id)])
  );
}
export function dailyChecklist(progress: Progress, date: string, mapDone: boolean) {
  const selected = selectedSubjects(progress, date);
  const completed = selected.filter((subject) =>
    subjectComplete(progress, date, subject, mapDone),
  ).length;
  return { selected, completed, done: selected.length > 0 && completed === selected.length };
}

/** Uses a small metadata index so the home page doesn't load map polygons. */
export function readMapCompletions(storage: Pick<Storage, 'getItem'>): Record<string, boolean> {
  return Object.fromEntries(
    Object.entries(mapIndex).map(([date, map]) => {
      let done = false;
      try {
        const value = JSON.parse(storage.getItem(map.storageKey) ?? 'null');
        done =
          value &&
          Number.isInteger(value.stage) &&
          value.stage >= map.finalStage &&
          value.stage <= map.finalStage + 1 &&
          value.solved &&
          typeof value.solved === 'object' &&
          !Array.isArray(value.solved) &&
          map.placeIds.every((id) =>
            ['named', 'revealed'].includes(value.solved[`${map.finalStage}/${id}`]),
          );
      } catch {
        /* Map storage owns the recovery UI; never replace unreadable records here. */
      }
      return [date, !!done];
    }),
  );
}
